/**
 * Aufrufe der Servicezusagen -- F4-01 bis F4-08.
 *
 * Kalender und Zusagen sind Stammdaten; sie liegen trotzdem in einem eigenen
 * Modul und nicht bei `masterdata`. Der Grund ist ihr Aufbau: Die acht
 * Stammdatenarten teilen sich eine Maske aus Name, Vorgabe und Reihenfolge.
 * Ein Kalender hat Zeitfenster und Feiertage, eine Zusage hat Ziele je
 * Prioritaet -- die gemeinsame Maske traegt das nicht.
 */

import type { components } from '../../api/schema';
import { request } from '../../lib/apiClient';

export type BusinessCalendar = components['schemas']['BusinessCalendarRead'];
export type TimeWindow = components['schemas']['TimeWindowModel'];
export type Holiday = components['schemas']['HolidayRead'];
export type Sla = components['schemas']['SlaRead'];
export type SlaTarget = components['schemas']['SlaTargetRead'];
export type SlaStatus = components['schemas']['SlaStatusRead'];
export type SlaEvent = components['schemas']['SlaEventRead'];

export function listCalendars(): Promise<BusinessCalendar[]> {
  return request<BusinessCalendar[]>('/business-calendars');
}

export function createCalendar(payload: Record<string, unknown>): Promise<BusinessCalendar> {
  return request<BusinessCalendar>('/business-calendars', { method: 'POST', body: payload });
}

export function updateCalendar(
  id: string,
  payload: Record<string, unknown>,
): Promise<BusinessCalendar> {
  return request<BusinessCalendar>(`/business-calendars/${id}`, {
    method: 'PATCH',
    body: payload,
  });
}

export function deleteCalendar(id: string): Promise<undefined> {
  return request<undefined>(`/business-calendars/${id}`, { method: 'DELETE' });
}

export function listHolidays(calendarId: string): Promise<Holiday[]> {
  return request<Holiday[]>(`/business-calendars/${calendarId}/holidays`);
}

export function createHoliday(
  calendarId: string,
  payload: Record<string, unknown>,
): Promise<Holiday> {
  return request<Holiday>(`/business-calendars/${calendarId}/holidays`, {
    method: 'POST',
    body: payload,
  });
}

export function deleteHoliday(calendarId: string, holidayId: string): Promise<undefined> {
  return request<undefined>(`/business-calendars/${calendarId}/holidays/${holidayId}`, {
    method: 'DELETE',
  });
}

export function listSlas(): Promise<Sla[]> {
  return request<Sla[]>('/slas');
}

export function createSla(payload: Record<string, unknown>): Promise<Sla> {
  return request<Sla>('/slas', { method: 'POST', body: payload });
}

export function updateSla(id: string, payload: Record<string, unknown>): Promise<Sla> {
  return request<Sla>(`/slas/${id}`, { method: 'PATCH', body: payload });
}

export function deleteSla(id: string): Promise<undefined> {
  return request<undefined>(`/slas/${id}`, { method: 'DELETE' });
}

export function listSlaEvents(ticketId: string): Promise<SlaEvent[]> {
  return request<SlaEvent[]>(`/tickets/${ticketId}/sla-events`);
}

/**
 * Stoesst den Waechter von Hand an -- F4-06, F4-07.
 *
 * Derselbe Lauf, den der Hintergrunddienst jede Minute macht. Er ist kein
 * Ersatz dafuer: Ohne den Dienst warnte niemand, solange keiner hinsieht.
 * Er ist fuer den Augenblick nach dem Einrichten einer Zusage -- man will
 * wissen, ob die Uhr laeuft, und nicht bis zur naechsten Minute raten.
 */
export function runSlaWatchdog(): Promise<WatchdogResult> {
  return request<WatchdogResult>('/slas/actions/run-watchdog', { method: 'POST' });
}

export interface WatchdogResult {
  checked: number;
  warnings: number;
  breaches: number;
}
